import React, { useState, useEffect, useRef } from 'react';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import GridWrapper from '../../components/common/GridWrapper/GridWrapper';

function DocumentOCRViewer({ imageUrl, ocrText, isArabic = false }) {
  const scrollRef = useRef(null);

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (scrollElement) {
      scrollElement.scrollTop = (scrollElement.scrollHeight - scrollElement.clientHeight) / 2;
      scrollElement.scrollLeft = (scrollElement.scrollWidth - scrollElement.clientWidth) / 2;
    }
  }, []);

  // Arabic text styling
  const arabicTextStyle = isArabic
    ? {
        direction: 'rtl',
        textAlign: 'right',
        fontFamily: 'Arial, "Traditional Arabic", "Simplified Arabic", Tahoma, sans-serif',
        lineHeight: 2,
        fontSize: '16px'
      }
    : {
        direction: 'ltr',
        textAlign: 'left'
      };

  return (
    <GridWrapper>
      <Grid container spacing={2} sx={{ paddingTop: { xs: 2, md: 5 } }}>
        {/* Document Viewer */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            background: `linear-gradient(to bottom, #eae8ed, #ffffff)`,
            padding: 2
          }}
        >
          <div className="App">
            <Box
              sx={{
                width: '100%',
                height: { xs: '400px', sm: '500px', md: '700px', lg: '900px' }
              }}
            >
              <div
                ref={scrollRef}
                style={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  overflow: 'auto',
                  border: '1px solid #ddd',
                  borderRadius: '8px'
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    minHeight: '100%'
                  }}
                >
                  <img
                    src={imageUrl}
                    alt="Document"
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block'
                    }}
                  />
                </div>
              </div>
            </Box>
          </div>
        </Grid>

        {/* Extracted OCR Text */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            background: `linear-gradient(to bottom, #eae8ed, #ffffff)`,
            padding: 2
          }}
        >
          <Box sx={{ width: '100%', typography: 'body1' }}>
            {/* Header row with title on left and Copy button on right */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2
              }}
            >
              <Box
                sx={{
                  border: 2,
                  borderRadius: '10px',
                  background: 'linear-gradient(to right, #432F60, #9E3C5D )',
                  color: '#ffffff',
                  textAlign: 'center',
                  padding: { xs: '8px 16px', sm: '10px 20px' },
                  fontWeight: 'bold',
                  fontSize: { xs: '14px', sm: '16px' }
                }}
              >
                Extracted OCR Text
              </Box>

              <Button
                variant="outlined"
                onClick={() => navigator.clipboard.writeText(ocrText)}
                disabled={!ocrText}
                sx={{
                  color: '#ffffff',
                  padding: { xs: '6px 16px', sm: '8px 24px' },
                  fontSize: { xs: '12px', sm: '14px' },
                  border: '1px solid #674f87',
                  borderRadius: '10px',
                  background: 'linear-gradient(to right, #674f87 1%, #ef798a 200%)',
                  '&:hover': {
                    background: 'linear-gradient(to top right, #ef798a 1%, #674f87)',
                    border: '1px solid #674f87',
                    color: '#ffffff'
                  },
                  '&:disabled': {
                    background: '#cccccc',
                    color: '#666666',
                    border: '1px solid #999999'
                  }
                }}
              >
                Copy Text
              </Button>
            </Box>

            <div
              style={{
                height: 'auto',
                maxHeight: '680px',
                width: '100%'
              }}
            >
              <TextField
                multiline
                fullWidth
                value={ocrText || ''}
                InputProps={{
                  readOnly: true,
                  sx: {
                    height: { xs: '300px', sm: '400px', md: '600px', lg: '680px' },
                    alignItems: 'flex-start',
                    background: 'linear-gradient(to right, #eae8ed, #ffffff)',
                    ...arabicTextStyle,
                    fontSize: { xs: '14px', sm: '16px' }
                  }
                }}
                placeholder={'OCR text will appear here...'}
                variant="outlined"
                sx={{
                  height: '100%',
                  '& .MuiOutlinedInput-root': {
                    height: '100%'
                  }
                }}
              />
            </div>
          </Box>
        </Grid>
      </Grid>
    </GridWrapper>
  );
}

export default DocumentOCRViewer;
